import {useState, useEffect} from "react";
import { FiClock, } from "react-icons/fi";
import { useTranslation } from "../hooks/useTranslation";

export default function BuyBackButton({ openedAt, buyBackExpirateTime, poolOrderId, isOwned, onReceived, onBuyBack, onIsEndedChange}) {
  const [remainingTime, setRemainingTime] = useState(null);
  const [isEnded, setIsEnded] = useState(false);
  const { t } = useTranslation();
  
  // Parse duration string (e.g., "1h", "30m", "2h 30m") to milliseconds
  const parseDurationToMs = (durationStr) => {
    if (!durationStr) return 0;
    
    let totalMs = 0;
    // Match patterns like "1h", "30m", "2h 30m", etc.
    const hourMatch = durationStr.match(/(\d+)h/);
    const minuteMatch = durationStr.match(/(\d+)m/);
    
    if (hourMatch) {
      totalMs += parseInt(hourMatch[1]) * 60 * 60 * 1000; // hours to milliseconds
    }
    if (minuteMatch) {
      totalMs += parseInt(minuteMatch[1]) * 60 * 1000; // minutes to milliseconds
    }
    
    return totalMs;
  };

  // Calculate remaining time
  useEffect(() => {
    if (!openedAt || !buyBackExpirateTime) {
      setIsEnded(true);
      if (onIsEndedChange && poolOrderId != null) {
        onIsEndedChange(poolOrderId, true);
      }
      return;
    }

    const calculateRemaining = () => {
      // Convert openedAt to Date (handle both Unix timestamp in seconds and milliseconds)
      const openedAtDate = typeof openedAt === 'string' 
        ? new Date(openedAt) 
        : new Date(typeof openedAt === 'number' && openedAt < 1000000000000 ? openedAt * 1000 : openedAt);
      
      // Parse duration to milliseconds
      const durationMs = parseDurationToMs(buyBackExpirateTime);
      
      // Calculate expiration time
      const expirationTime = openedAtDate.getTime() + durationMs;
      
      // Calculate remaining time
      const now = Date.now();
      const remaining = expirationTime - now;
      
      if (remaining <= 0) {
        setIsEnded(true);
        setRemainingTime(0);
        if (onIsEndedChange && poolOrderId != null) {
          onIsEndedChange(poolOrderId, true);
        }
      } else {
        setIsEnded(false);
        setRemainingTime(remaining);
        if (onIsEndedChange && poolOrderId != null) {
          onIsEndedChange(poolOrderId, false);
        }
      }
    };

    // Calculate immediately
    calculateRemaining();

    // Update every second
    const timer = setInterval(calculateRemaining, 1000);

    return () => clearInterval(timer);
  }, [openedAt, buyBackExpirateTime, poolOrderId, onIsEndedChange]);

  // Format remaining time to readable string
  const formatRemainingTime = (ms) => {
    if (!ms || ms <= 0) return "0s";
    
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (remainingHours > 0) parts.push(`${remainingHours}h`);
    if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
    if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
    
    return parts.join(" ");
  };

  const handleClick = () => {
    if (isEnded) {
      // When timer ended, call the received callback
      // Check for null/undefined, not truthiness (because 0 is falsy but valid)
      if (onReceived && poolOrderId != null) {
        onReceived(poolOrderId);
      }
      return;
    }
    if (onBuyBack && poolOrderId != null) {
      onBuyBack(poolOrderId);
    }
  };

  return (
    <button
        disabled={false}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (!isEnded && !isOwned) {
            e.currentTarget.style.background = "rgba(193, 167, 255, 0.1)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isEnded && !isOwned) {
            e.currentTarget.style.background = "transparent";
          }
        }}
        style={{
          width: "100%",
          padding: "5px 12px",
          borderRadius: 15,
          border: isOwned 
            ? "1px solid rgba(255, 255, 255, 0.2)" 
            : isEnded 
            ? "1px solid rgba(151, 112, 221, 0.6)" 
            : "1px solid rgba(193, 167, 255, 1)",
          background: isOwned 
            ? "rgba(255, 255, 255, 0.05)" 
            : isEnded 
            ? "#8572c3" 
            : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection:"column",
          fontSize: "14px",
          fontWeight: "bold",
          opacity: isOwned ? 0.7 : 1,
          cursor: isOwned ? "default" : "pointer",
          transition: "transform, opacity, background-color, border-color, color 0.2s ease",
        }}
    >
      {isOwned ? (
        <span style={{ color: "rgba(255, 255, 255, 0.6)" }}>{t("buyPack.owned")}</span>
      ) : isEnded ? (
        <span style={{ color: "#000000" }}>{t("buyPack.received")}</span>
      ) : (
        <>
          <span>{t("buyPack.buyBack")}</span>
          {remainingTime !== null && (
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                opacity: 0.7,
              }}
              className="flex items-center gap-[3px]"
            >
              <FiClock />
              {formatRemainingTime(remainingTime)}
            </span>
          )}
        </>
      )}
    </button>
  );
}